import { Typography, Box, Divider, Stack, Grid, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ArtistCard from '../../components/card/ArtistCard';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';
import NewsCard from '../../components/card/NewsCard';
import PlaylistCard from '../../components/card/PlaylistCard';
import NoticeCard from '../../components/card/NoticeCard';
import theme from '../../theme';
import { categoryFormatter } from '../../utils/formatter';
import NoData from '../../components/common/NoData';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import { REFERRER_TYPE } from '../../constants/gaEnums';

const SearchResultSection = ({ title, count, items }) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const getCardComponent = (title, item) => {
    switch (title) {
      case 'Artists':
        return (
          <ArtistCard
            key={item.id}
            id={item.id}
            name={item.name}
            artistDbId={item.artistId}
            artistImageUrl={item.artistImageUrl}
            artistSource={item.artistSource}
            slug={item.slug}
            referrerType={REFERRER_TYPE.SEARCH}
          />
        );
      case 'Reviews':
        return (
          <AlbumVerticalCard
            key={item.sourceId}
            albumId={item.albumId}
            sourceId={item.sourceId}
            albumSource={item.albumSource}
            slug={item.slug}
            imageUrl={item.albumImageUrl}
            albumName={item.title}
            contentRating={item.contentRating}
            artistName={item.artist.name}
            artistId={item.artist.sourceId}
            artistDbId={item.artist.id}
            artistSource={item.artist.artistSource}
            artistSlug={item.artist.slug}
            albumAvgRating={item.averageRating}
            commentCount={item.commentCount}
            bookmarkCount={item.bookmarkCount}
            releaseDate={item.releaseDate}
            isStarred={item.isStarred}
            hideBookmarkCount
            hideReviewMetaOnMobile
            ratingSolo={!isLg}
            dateSolo={isXs}
            smallText
            referrerType={REFERRER_TYPE.SEARCH}
          />
        );
      case 'News':
        return (
          <NewsCard
            key={item.id}
            news={item}
            imageMaxWidth={180}
            referrerType={REFERRER_TYPE.SEARCH}
          />
        );
      case 'Lists':
      case 'Mixes':
        return (
          <PlaylistCard
            key={item.id}
            playlist={item}
            dateOnNewLine={isXs}
            thumbnailVariant="square"
            referrerType={REFERRER_TYPE.SEARCH}
          />
        );
      case 'Notices':
        return (
          <NoticeCard
            key={item.id}
            notice={item}
            imageMaxWidth={180}
            referrerType={REFERRER_TYPE.SEARCH}
          />
        );
      default:
        return null;
    }
  };

  const isGridLayout = title === 'Artists' || title === 'Reviews';

  const handleViewAllClick = () => {
    const categoryPath = title === 'Mixes' ? 'lists' : title.toLowerCase();
    navigateWithScrollMemory(`/search/${categoryPath}?q=${encodeURIComponent(query)}`);
  };

  return (
    <Box sx={{ pb: { xs: '20px', md: 0 } }}>
      {/* 카테고리 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: { xs: 1, md: 2 },
          borderBottom: `1px solid ${theme.palette.grey.line}`,
          mb: { xs: 2, md: 2.5, lg: 3.75 },
        }}
      >
        <Typography fontSize={{ xs: 18, md: 20, lg: 24 }} fontWeight={700}>
          {categoryFormatter(title)} ({count >= 25 ? '25+' : count})
        </Typography>
        {count > 0 && (
          <Typography
            color={theme.palette.black[4]}
            fontSize={{ xs: 13, md: 14 }}
            onClick={handleViewAllClick}
            sx={{ cursor: 'pointer' }}
          >
            더보기
          </Typography>
        )}
      </Box>

      {items.length === 0 && <NoData message="결과 없음" paddingTop={0} />}

      {/* 카드 목록 */}
      {isGridLayout ? (
        <Grid container columnSpacing={{ xs: 1.5, md: 2, lg: 2.5 }}>
          {items.slice(0, isXs ? 3 : 4).map((item) => (
            <Grid item xs={4} md={3} key={item.id || item.sourceId}>
              {getCardComponent(title, item)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Stack spacing={{ xs: 1.875, md: 2.5, lg: 3.75 }}>
          {items.slice(0, isXs ? 3 : 4).map((item) => (
            <Box key={item.id || item.sourceId}>
              {getCardComponent(title, item)}
              <Divider
                sx={{
                  display: { xs: 'block', md: 'none' },
                  borderColor: theme.palette.grey.line,
                  mt: 1.875,
                }}
              />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default SearchResultSection;
